
/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useState, useEffect, useMemo, createContext } from 'react'
import { useQuery } from 'react-query'
import { ComponentRequest } from './componentLayout'
import { useQueryRequest } from './request'
import {
    initialQueryResponse,
    initialQueryState,
    stringifyRequestQuery,
} from '../../_metronic/helpers'
import Swal from 'sweetalert2'
import { checkAndRemoveSessions } from './helper'

/**
 * QueryResponseProvider & related hooks
 *
 * Provides React context and hooks for managing API response state, loading, pagination, and error handling in the application.
 *
 * Features:
 * - Uses react-query for API data fetching and caching
 * - Handles session expiration and error states with SweetAlert2 dialogs
 * - Provides hooks for accessing response data, pagination, count, and loading state
 * - Integrates with global query request state
 *
 * Exports:
 * - QueryResponseProvider: Context provider for API response state
 * - useQueryResponse: Hook to access response context
 * - useQueryResponseData: Hook to get response data
 * - useQueryResponsePagination: Hook to get pagination info
 * - useQueryResponseLoading: Hook to get loading state
 * - useQueryResponseCount: Hook to get count info
 */
//const initialQueryResponse = { refetch: () => { }, isLoading: false, query: '' }

const QueryResponseContext = createContext(initialQueryResponse)

const QueryResponseProvider = (props) => {
    const { state } = useQueryRequest()

    const [query, setQuery] = useState(stringifyRequestQuery(state))
    const updatedQuery = useMemo(() => stringifyRequestQuery(state), [state])
    const { comRequest } = useContext(ComponentRequest)
    useEffect(() => {
        if (query !== updatedQuery) {
            setQuery(updatedQuery)
        }
    }, [updatedQuery])
    const {
        isFetching,
        refetch,
        data: response,
        error,
    } = useQuery(
        `${comRequest.type}-${query}`,
        () => props.getApi(query),

        {
            cacheTime: 0,
            keepPreviousData: true,
            refetchOnWindowFocus: false,
            onError: (error) => {
                if (error.request.status === 401) {
                    // Handle unauthorized error, e.g., redirect to login or logout
                    Swal.fire({
                        title: 'Login session expried',
                        //text: dataMessage,
                        icon: "error",
                        //dangerMode: true,
                    }).then(result => {
                        document.location.reload()
                    }).catch(error => {
                        console.error(error);
                    });
                    // Function to handle logout

                } else if (error.status === 500) {
                    // Handle server error
                } else {
                }

            }
        }
    )

    useEffect(() => {
        // Check for special messages in the error response
        if (response && response.message === 'Api Error') {
            Swal.fire({
                title: 'Your session timed out',
                text: 'You will be redirected to admin',
                icon: 'warning',
                confirmButtonText: 'Ok'
            }).then((result) => {
                if (result.isConfirmed) {
                    checkAndRemoveSessions();
                }
            });

        }
    }, [response]);

    return (
        <QueryResponseContext.Provider value={{ isLoading: isFetching, refetch, response, error, query }}>
            {props.children}
        </QueryResponseContext.Provider>
    )
}

const useQueryResponse = () => useContext(QueryResponseContext)

const useQueryResponseData = () => {
    const { response } = useQueryResponse()

    if (!response) {
        return []
    }

    return response?.data || []
}

const useQueryResponsePagination = () => {
    const defaultPaginationState = {
        links: [],
        ...initialQueryState,
    }

    const { response } = useQueryResponse()
    if (!response || !response.payload || !response.payload.pagination) {
        return defaultPaginationState
    }

    return response.payload.pagination
}

const useQueryResponseCount = () => {
    const defaultCountState = {
        count: [],
        ...initialQueryState,
    }

    const { response } = useQueryResponse()
    if (!response || !response.count || !response.count) {
        return defaultCountState
    }

    return response.count
}

const useQueryResponseLoading = () => {
    const { isLoading } = useQueryResponse()
    return isLoading
}

export {
    QueryResponseProvider,
    useQueryResponse,
    useQueryResponseData,
    useQueryResponsePagination,
    useQueryResponseLoading,
    useQueryResponseCount,
}
